import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import cn from "classnames";
import { Select, SelectItem } from "components/UI/Select";
import type { FormMultiSelectPath, FormValues } from "types/form.types";
import { SelectOption } from "types/select.types";
import { mapNamesToSelectOptions } from "utils/select.utils";
import styles from "./RequestTypeSelect.module.scss";

type RequestTypeSelectProps<TFieldName extends FormMultiSelectPath = FormMultiSelectPath> = {
  fieldName: TFieldName;
  label: string;
  placeholder: string;
  options: readonly SelectOption[];
  className?: string;
  rules?: RegisterOptions<FormValues, TFieldName>;
};

export const RequestTypeSelect = <TFieldName extends FormMultiSelectPath>({
  fieldName,
  label,
  placeholder,
  options,
  className,
  rules,
}: RequestTypeSelectProps<TFieldName>) => {
  const { control } = useFormContext<FormValues>();

  const parseUserData = <T,>(dataJson: string): T => {
    return JSON.parse(dataJson) as T;
  };

  return (
    <div className={cn(styles.requestTypeSelect, className)}>
      <Controller
        rules={rules}
        control={control}
        name={fieldName}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const selectedOptions = mapNamesToSelectOptions(value, options);

          return (
            <Select
              multiple
              withCheckbox
              selectValue={selectedOptions}
              label={label}
              placeholder={placeholder}
              error={!!error}
              errorMessage={error?.message}
              onChange={(selected) => {
                const updatedValues = parseUserData<SelectOption[]>(selected.value);
                onChange(updatedValues.map((item) => item.value));
              }}
            >
              {options.map((option) => (
                <SelectItem key={option.name} name={option.name} value={option.value}>
                  {option.value}
                </SelectItem>
              ))}
            </Select>
          );
        }}
      />
    </div>
  );
};
